/**
 * Formatea fecha/hora según zona horaria y formato configurado.
 * Se usa en facturas, órdenes, reportes PDF y toda la app.
 */

export type FormatoFecha = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
export type FormatoHora = '24' | '12';

/** YYYY-MM-DD puro (día civil, sin hora). */
function parseCivilDateParts(
  value: string
): { year: string; month: string; day: string } | null {
  const m = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(year) || month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year: m[1], month: m[2], day: m[3] };
}

function formatCivilParts(
  parts: { year: string; month: string; day: string },
  dateFormat: FormatoFecha
): string {
  switch (dateFormat) {
    case 'MM/DD/YYYY':
      return `${parts.month}/${parts.day}/${parts.year}`;
    case 'YYYY-MM-DD':
      return `${parts.year}-${parts.month}-${parts.day}`;
    default:
      return `${parts.day}/${parts.month}/${parts.year}`;
  }
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const raw = String(value).trim();
  if (!raw) return null;
  // Día civil YYYY-MM-DD: NO usar new Date('YYYY-MM-DD') (= medianoche UTC → día anterior en Managua).
  // Mediodía UTC mantiene el mismo día calendario en zonas de UTC-12 a UTC+12.
  const civil = parseCivilDateParts(raw);
  if (civil) {
    const noon = new Date(`${civil.year}-${civil.month}-${civil.day}T12:00:00.000Z`);
    return Number.isNaN(noon.getTime()) ? null : noon;
  }
  // SQLite CURRENT_TIMESTAMP / datetime naive: hora UTC sin sufijo Z.
  // Sin esto, el navegador lo trata como hora local y todo se ve +6h (AM→PM en Managua).
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?(\.\d{1,3})?$/.test(raw) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)) {
    const iso = raw.includes('T') ? `${raw}Z` : `${raw.replace(' ', 'T')}Z`;
    const asUtc = new Date(iso);
    return Number.isNaN(asUtc.getTime()) ? null : asUtc;
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Formatea solo la fecha en la zona horaria indicada.
 * Si el valor es YYYY-MM-DD (sin hora), se trata como día civil y no se desplaza por TZ.
 */
export function formatDate(
  value: string | Date | null | undefined,
  timeZone: string = 'America/Managua',
  dateFormat: FormatoFecha = 'DD/MM/YYYY'
): string {
  if (typeof value === 'string') {
    const civil = parseCivilDateParts(value);
    if (civil) return formatCivilParts(civil, dateFormat);
  }
  const d = toDate(value);
  if (!d) return '';
  const day = new Intl.DateTimeFormat('es-ES', { timeZone, day: '2-digit' }).format(d);
  const month = new Intl.DateTimeFormat('es-ES', { timeZone, month: '2-digit' }).format(d);
  const year = new Intl.DateTimeFormat('es-ES', { timeZone, year: 'numeric' }).format(d);
  switch (dateFormat) {
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    default:
      return `${day}/${month}/${year}`;
  }
}

/**
 * Formatea solo la hora en la zona horaria indicada (24h o 12h).
 */
export function formatTime(
  value: string | Date | null | undefined,
  timeZone: string = 'America/Managua',
  horaFormat: FormatoHora = '24'
): string {
  const d = toDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat('es-ES', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: horaFormat === '12',
  }).format(d);
}

/**
 * Formatea fecha y hora (para listados, PDFs, etc.).
 */
export function formatDateTime(
  value: string | Date | null | undefined,
  timeZone: string = 'America/Managua',
  dateFormat: FormatoFecha = 'DD/MM/YYYY',
  horaFormat: FormatoHora = '24'
): string {
  const dateStr = formatDate(value, timeZone, dateFormat);
  const timeStr = formatTime(value, timeZone, horaFormat);
  if (!dateStr || !timeStr) return dateStr || timeStr;
  return `${dateStr} ${timeStr}`;
}
